import { useMemo } from 'react';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { useCalendarStore } from '@/stores/use-calendar-store';

/** A context-aware starter prompt shown on the chat home. */
export interface ChatSuggestion {
  /** Stable identity for React keys + tests. */
  key: 'open-docs' | 'calendar' | 'last-week';
  /** Material symbol icon name. */
  icon: string;
  /** Card title (short). */
  title: string;
  /** Context-aware secondary line (real data or a generic fallback). */
  subtitle: string;
  /** The exact text sent to the agent when clicked. */
  prompt: string;
  /** Token expression for the icon tile color (a pastel, deepened in CSS). */
  color: string;
}

/**
 * Derives 3 context-aware starter prompts from real workspace signals. Read-only
 * — never mutates. Each suggestion always renders (a generic subtitle covers the
 * no-context case) so the home always offers a way in.
 */
export function useChatSuggestions(): ChatSuggestion[] {
  const docs = useWorkspaceDocs();
  const eventsByDate = useCalendarStore((s) => s.events);

  return useMemo<ChatSuggestion[]>(() => {
    // open-docs: name the most-recently-updated non-archived doc.
    const active = docs.filter((d) => !d.isArchived);
    const mostRecent = [...active].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )[0];
    const docsSubtitle = mostRecent?.title?.trim() ? mostRecent.title : 'Across your workspace';

    // calendar: count events across all loaded date buckets.
    const eventCount = Object.values(eventsByDate).reduce((n, list) => n + list.length, 0);
    const calSubtitle =
      eventCount > 0
        ? `${eventCount} ${eventCount === 1 ? 'event' : 'events'} this week`
        : 'Your upcoming events';

    return [
      {
        key: 'open-docs',
        icon: 'description',
        title: 'Summarize my docs',
        subtitle: docsSubtitle,
        prompt: 'Summarize what I have open',
        color: 'var(--pastel-mist-blue)',
      },
      {
        key: 'calendar',
        icon: 'calendar_month',
        title: "What's on my calendar?",
        subtitle: calSubtitle,
        prompt: "What's on my calendar this week?",
        color: 'var(--pastel-lavender)',
      },
      {
        key: 'last-week',
        icon: 'forum',
        title: 'What did I work on?',
        subtitle: 'Across docs, notes & meetings',
        prompt: 'What did I work on this week?',
        color: 'var(--pastel-sage)',
      },
    ];
  }, [docs, eventsByDate]);
}
